import SearchIcon from "@mui/icons-material/Search";
import { Dialog, InputAdornment, List, ListItemButton, ListItemText, ListSubheader, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGlobalSearch } from "./useData";

/** Ctrl/Cmd+K command palette over /api/v1/search/ (CF-0034). */
export function SearchPalette({ open, onClose, onOpen }: { open: boolean; onClose: () => void; onOpen: () => void }) {
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const search = useGlobalSearch(q);
  const flat = (search.data ?? []).flatMap((g) => g.results);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); onOpen(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpen]);

  useEffect(() => { if (!open) { setQ(""); setCursor(0); } }, [open]);

  const go = (url: string) => { onClose(); navigate(url); };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { position: "fixed", top: 80 } } }}>
      <TextField
        autoFocus fullWidth placeholder={`${t("common.search")}…`} value={q}
        onChange={(e) => { setQ(e.target.value); setCursor(0); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, flat.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
          if (e.key === "Enter" && flat[cursor]) go(flat[cursor].url);
        }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>, sx: { borderRadius: 0 } } }}
        inputProps={{ "aria-label": t("common.search"), role: "combobox", "aria-expanded": flat.length > 0 }}
      />
      <List dense sx={{ maxHeight: 400, overflow: "auto" }} role="listbox">
        {q.trim().length >= 2 && !search.isFetching && flat.length === 0 && (
          <Typography sx={{ p: 2 }} color="text.secondary">{t("common.noResults")}</Typography>
        )}
        {(search.data ?? []).map((group) => (
          <li key={group.entity_type}>
            <ul style={{ padding: 0 }}>
              <ListSubheader>{group.label}</ListSubheader>
              {group.results.map((r) => {
                const idx = flat.indexOf(r);
                return (
                  <ListItemButton key={r.entity_id} selected={idx === cursor} onClick={() => go(r.url)} role="option" aria-selected={idx === cursor}>
                    <ListItemText primary={r.number ? `${r.number} · ${r.title}` : r.title} secondary={r.subtitle} />
                  </ListItemButton>
                );
              })}
            </ul>
          </li>
        ))}
      </List>
    </Dialog>
  );
}
